import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Plus, Search } from "lucide-react";
import { getErrorMessage, isNotFoundError } from "@/services/api";
import { Invitation } from "@/services/invitations";
import { ManagedUser, ManagedUserRole, USER_LOGINS } from "@/services/users";
import {
  useDeleteManagedUser,
  useUpdateInvitation,
  useManagedUsersQuery,
  usePendingInvitationsQuery,
  useResendInvitation,
  useRevokeInvitation,
  useUpdateManagedUser,
  useUserCountsQuery,
} from "@/hooks/queries";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useDebouncedValue } from "@/components/application/merchant-tasks/config/useDebouncedValue";
import ConfirmDialog, { ConfirmRequest } from "@/components/common/ConfirmDialog";
import LockedControl from "@/components/common/LockedControl";
import CountChip from "@/components/common/CountChip";
import ListPager from "@/components/common/ListPager";
import NoticeAlert, { Notice } from "@/components/common/NoticeAlert";
import TrialLocked from "@/components/trial/TrialLocked";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup } from "@/components/ui/toggle-group";
import SegmentedChips, { ChipItem } from "./SegmentedChips";
import UsersTable from "./UsersTable";
import InvitationsTable from "./InvitationsTable";
import { deactivationWarning } from "./membershipCopy";
import { fromList } from "./listReturn";
import { USER_PAGES } from "./userPages";

const SEARCH_DEBOUNCE_MS = 300;
const NO_ORGANIZATION = "You have no organization to invite people into.";
/** Chip value that switches the table from users to pending invitations. */
const PENDING_INVITES = "pending-invites";
const LOGIN_OPTIONS = [
  { value: "registered", label: "Registered" },
  { value: "email_only", label: "Email only" },
] as const;

/** Search, login chips, the invites chip, and the page, kept in the URL
 * (`?q=&login=&view=invites&page=`). Changing a filter returns to the first page. */
function useListFilters(hasLogins: boolean) {
  const [searchParams, setSearchParams] = useSearchParams();
  const showInvites = searchParams.get("view") === "invites";
  // Only brokers have the login filter, and invitations have no login.
  const rawLogins = hasLogins && !showInvites ? (searchParams.get("login") ?? "") : "";
  const logins = useMemo(
    () => USER_LOGINS.filter((login) => rawLogins.split(",").includes(login)),
    [rawLogins]
  );
  const search = searchParams.get("q") ?? "";
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const update = useCallback(
    (changes: Record<string, string>) =>
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          // `org` is no longer a filter; a stale one from an old link goes with the next change.
          for (const [key, value] of Object.entries({ page: "", org: "", ...changes })) {
            if (value) next.set(key, value);
            else next.delete(key);
          }
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );
  // Invitations have no login, so Pending invites and the login chips exclude each other.
  const setChips = (chips: string[]) => {
    const nextLogins = chips.filter((chip) => chip !== PENDING_INVITES);
    const invites = showInvites
      ? chips.includes(PENDING_INVITES) && !nextLogins.length
      : chips.includes(PENDING_INVITES);
    update({ login: invites ? "" : nextLogins.join(","), view: invites ? "invites" : "" });
  };
  const setSearch = useCallback((q: string) => update({ q }), [update]);
  const setPage = useCallback(
    (next: number) => update({ page: next > 1 ? String(next) : "" }),
    [update]
  );
  const clearFilters = () => update({ q: "", login: "" });

  const chips: string[] = showInvites ? [...logins, PENDING_INVITES] : [...logins];
  return {
    logins,
    showInvites,
    chips,
    setChips,
    search,
    setSearch,
    page,
    setPage,
    clearFilters,
  };
}

/** The Brokers, Underwriters, or Admin Support page. */
const UserList: React.FC<{ role: ManagedUserRole }> = ({ role }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: manager } = useCurrentUser();
  // Everything here is about the manager's default organization; without one there is nothing.
  const hasOrganization = Boolean(manager?.organization_name);
  const isBrokers = role === "broker";
  const { title, inviteTitle, path } = USER_PAGES[role];
  const { logins, showInvites, chips, setChips, search, setSearch, page, setPage, clearFilters } =
    useListFilters(isBrokers);
  // `q` in the URL is the source of truth; the input only writes it when the user types.
  const [searchInput, setSearchInput] = useState(search);
  const debouncedSearch = useDebouncedValue(searchInput.trim(), SEARCH_DEBOUNCE_MS);
  const syncedSearch = useRef(search);
  useEffect(() => {
    if (debouncedSearch === syncedSearch.current) return;
    syncedSearch.current = debouncedSearch;
    setSearch(debouncedSearch);
  }, [debouncedSearch, setSearch]);
  // Clear filters, a sidebar click, or Back/Forward changed `q`: show it in the input.
  useEffect(() => {
    if (search === syncedSearch.current) return;
    syncedSearch.current = search;
    setSearchInput(search);
  }, [search]);

  const filters = { role, search, page };
  const usersQuery = useManagedUsersQuery({ ...filters, logins }, !showInvites);
  const invitesQuery = usePendingInvitationsQuery(filters, showInvites);
  const { data: counts } = useUserCountsQuery();
  const pendingCount = counts?.[role].pending_invites;
  const activeQuery = showInvites ? invitesQuery : usersQuery;
  // A revoke can empty the last page; step back instead of showing an error.
  const pageGone = activeQuery.isError && isNotFoundError(activeQuery.error) && page > 1;
  useEffect(() => {
    if (pageGone) setPage(page - 1);
  }, [pageGone, page, setPage]);
  const loadError =
    activeQuery.isError && !pageGone ? (
      <>
        {getErrorMessage(activeQuery.error)}
        <Button
          variant="link"
          className="ml-1 h-auto p-0"
          onClick={() => void activeQuery.refetch()}
        >
          Retry
        </Button>
      </>
    ) : null;
  const updateUser = useUpdateManagedUser();
  const deleteUser = useDeleteManagedUser();
  const updateInvitation = useUpdateInvitation();
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();

  const [notice, setNotice] = useState<Notice | null>(location.state?.notice ?? null);
  const [confirmRequest, setConfirmRequest] = useState<ConfirmRequest | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const dismissNotice = useCallback(() => setNotice(null), []);

  // A notice handed over by the invite page shows once, not again on reload.
  useEffect(() => {
    if (location.state?.notice) {
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location, navigate]);

  /** Runs `action` and reports it; true when it succeeded. */
  const run = async (action: () => Promise<unknown>, success: string) => {
    try {
      await action();
      setNotice({ kind: "success", text: success });
      return true;
    } catch (error) {
      setNotice({ kind: "error", text: getErrorMessage(error) });
      return false;
    }
  };

  const handleSetActive = (user: ManagedUser, active: boolean) => {
    const setActive = () =>
      run(
        () => updateUser.mutateAsync({ id: user.id, data: { is_active: active } }),
        `${user.email} was ${active ? "activated" : "deactivated"}.`
      );
    if (active) {
      setActive();
      return;
    }
    setConfirmRequest({
      title: "Deactivate user?",
      description: deactivationWarning(user),
      confirmLabel: "Deactivate",
      destructive: true,
      onConfirm: setActive,
    });
  };

  const handleDeleteUser = (user: ManagedUser) =>
    setConfirmRequest({
      title: `Delete ${user.email}?`,
      description: "This permanently removes this email-only broker. This can't be undone.",
      confirmLabel: "Delete",
      destructive: true,
      onConfirm: async () => {
        // The row (and its focused button) is gone; hand focus to the page heading.
        if (await run(() => deleteUser.mutateAsync(user.id), `${user.email} was deleted.`)) {
          headingRef.current?.focus();
        }
      },
    });

  const showEditError = useCallback((text: string) => setNotice({ kind: "error", text }), []);

  const handleSaveCompany = (user: ManagedUser, companyName: string) =>
    updateUser.mutateAsync({ id: user.id, data: { company_name: companyName } });

  const handleChangeEmail = async (invitation: Invitation, email: string) => {
    await updateInvitation.mutateAsync({ id: invitation.id, data: { email } });
    setNotice({ kind: "success", text: `Invitation sent to ${email} with a new link.` });
  };

  const handleResend = (invitation: Invitation) =>
    setConfirmRequest({
      title: "Resend invitation?",
      description: `${invitation.email} gets the same invitation link again, with a new expiry date.`,
      confirmLabel: "Resend",
      onConfirm: () =>
        run(() => resend.mutateAsync(invitation.id), `Invitation resent to ${invitation.email}.`),
    });

  // Deleting revokes: the invitation leaves the list and its link dies, but the record stays.
  const handleDeleteInvitation = (invitation: Invitation) =>
    setConfirmRequest({
      title: `Delete invitation to ${invitation.email}?`,
      description: "It can no longer be accepted.",
      confirmLabel: "Delete",
      destructive: true,
      onConfirm: () => run(() => revoke.mutateAsync(invitation.id), "Invitation deleted."),
    });

  /** The invite button with its trial and no-organization locks; `compact` is the phone
   * "+ Invite", otherwise the full-width (below lg) toolbar button. */
  const inviteButton = (compact: boolean) => {
    const width = compact ? undefined : "w-full lg:w-auto";
    return (
      <TrialLocked lock="team" inline side="bottom" className={width}>
        <LockedControl
          locked={!hasOrganization}
          message={NO_ORGANIZATION}
          inline
          side="bottom"
          className={width}
        >
          <Button
            className={width}
            onClick={() => navigate(`${path}/new`, { state: fromList(location.search) })}
            aria-label={compact ? inviteTitle : undefined}
          >
            <Plus className="mr-2 h-4 w-4" />
            {compact ? "Invite" : inviteTitle}
          </Button>
        </LockedControl>
      </TrialLocked>
    );
  };

  const filtered = Boolean(search || (!showInvites && logins.length));
  const emptyState = (noneText: string, noMatchText: string) =>
    filtered ? (
      <>
        {noMatchText}
        <Button variant="link" className="ml-1 h-auto p-0" onClick={clearFilters}>
          Clear filters
        </Button>
      </>
    ) : (
      noneText
    );
  const busyInvitation = resend.isPending
    ? resend.variables
    : revoke.isPending
      ? revoke.variables
      : null;

  return (
    <div>
      <div className="mb-6 flex items-center gap-2.5">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl font-semibold tracking-tight outline-none"
        >
          {title}
        </h1>
        <CountChip count={counts?.[role].users} />
        {/* Phones: a short "+ Invite" at the right of the title. Only one copy is ever shown
            (the other is display:none), so assistive tech meets a single control. */}
        <div className="ml-auto md:hidden">{inviteButton(true)}</div>
      </div>

      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center">
        <div className="hidden md:contents">{inviteButton(false)}</div>
        <div className="relative flex-1 lg:min-w-[18rem]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-10"
            placeholder={
              showInvites
                ? "Search by email..."
                : isBrokers
                  ? "Search by name, email, or company..."
                  : "Search by name or email..."
            }
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            aria-label={`Search ${title.toLowerCase()}`}
          />
        </div>
        <ToggleGroup
          type="multiple"
          value={chips}
          onValueChange={setChips}
          className="flex-wrap justify-start gap-y-3"
          aria-label={`Filter ${title.toLowerCase()}`}
        >
          {isBrokers && (
            <>
              <SegmentedChips options={LOGIN_OPTIONS} />
              {/* Logins filter; Pending invites switches the table. Hidden where the row wraps. */}
              <Separator
                orientation="vertical"
                className="mx-3 hidden h-6 bg-muted-foreground/50 sm:block"
              />
            </>
          )}
          <ChipItem
            value={PENDING_INVITES}
            aria-label={`Pending invites${pendingCount ? `, ${pendingCount}` : ""}`}
          >
            <Mail aria-hidden />
            Pending invites
            <CountChip count={pendingCount} variant="badge" />
          </ChipItem>
        </ToggleGroup>
      </div>

      <NoticeAlert notice={notice} onDismiss={dismissNotice} floating />

      <Card className="overflow-hidden p-0">
        {showInvites ? (
          <>
            <InvitationsTable
              invitations={invitesQuery.data?.results ?? []}
              loading={invitesQuery.isLoading || pageGone}
              error={loadError}
              emptyMessage={emptyState(
                "No pending invites",
                "No pending invites match these filters."
              )}
              busyInvitationId={busyInvitation ?? null}
              showCompany={isBrokers}
              onChangeEmail={handleChangeEmail}
              onChangeCompany={(invitation, companyName) =>
                updateInvitation.mutateAsync({
                  id: invitation.id,
                  data: { company_name: companyName },
                })
              }
              onEditError={showEditError}
              onResend={handleResend}
              onDelete={handleDeleteInvitation}
            />
            {!loadError && <ListPager data={invitesQuery.data} onPageChange={setPage} />}
          </>
        ) : (
          <>
            <UsersTable
              users={usersQuery.data?.results ?? []}
              loading={usersQuery.isLoading || pageGone}
              error={loadError}
              showCompany={isBrokers}
              emptyMessage={emptyState(
                `No ${title.toLowerCase()} yet`,
                `No ${title.toLowerCase()} match these filters.`
              )}
              busyUserId={
                updateUser.isPending
                  ? updateUser.variables.id
                  : deleteUser.isPending
                    ? deleteUser.variables
                    : null
              }
              onSetActive={handleSetActive}
              onSaveCompany={handleSaveCompany}
              onEditError={showEditError}
              onDelete={handleDeleteUser}
            />
            {!loadError && <ListPager data={usersQuery.data} onPageChange={setPage} />}
          </>
        )}
      </Card>

      <ConfirmDialog request={confirmRequest} onClose={() => setConfirmRequest(null)} />
    </div>
  );
};

export default UserList;
